import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { LoadingService } from '@services/loading.service';
import { ImportDataCriteria, ImportCSVFileContent, ImportSchedule, ImportScheduleInfo, standardGetHttpOptions, standardHttpOptions, CompareSyncResults, ImportLogsSearchCriteria } from '@models';
import { throwError } from 'rxjs';
import { Observable } from 'rxjs';
import { KigSettings, KIG_SETTINGS } from '../../../settings/kig-settings';
import { UserService } from '@services/user.service';
import { of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { ImportSearchCriteria } from '@models/Import/ImportSearchCriteria.model';
import { ImportNowCriteria } from '@models/Import/importNowCriteria.model';
import moment from 'moment';

@Injectable()
export class ImportService {
  public standardHttpOptionsWithUser: any;

  private apiUrl: string;

  constructor(private http: HttpClient,
              private loadingService: LoadingService,
              private userService: UserService,
              @Inject(KIG_SETTINGS) private settings: KigSettings, ) {
    this.apiUrl = this.settings.apiUrl;
  }


  buildStandardHttpOptionsWithUser(username: string) {
    this.standardHttpOptionsWithUser = {
      withCredentials: true,
      headers: new HttpHeaders({
        ['Content-Type']: 'application/json',
        username
      })
    };
  }


  public getImportSchedules(): Observable<any> {
    this.loadingService.setMessage('Loading schedules ...');
    return this.http.get(`${ this.apiUrl }/import`, standardGetHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public getLastReadingDate(importSearchCriteria: ImportSearchCriteria): Observable<any> {
    return this.http.post(`${ this.apiUrl }/import/LastReadingDate/`, JSON.stringify(importSearchCriteria), standardHttpOptions).pipe(
      catchError(this.handleError)
    );
  }


  public updateImportSchedule(importSchedule: ImportScheduleInfo, username: string) {
    this.loadingService.setMessage('Updating schedule ...');
    this.buildStandardHttpOptionsWithUser(username);
    return this.http.put(`${ this.apiUrl }/import/`, JSON.stringify(importSchedule), this.standardHttpOptionsWithUser).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public createImportSchedule(importSchedule: ImportScheduleInfo) {
    this.loadingService.setMessage('Creating schedule ...');
    return this.http.post(`${ this.apiUrl }/import`, JSON.stringify(importSchedule), standardHttpOptions).pipe(
      map((response: Response) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public deleteImportSchedule(importScheduleId: number, username: string) {
    this.loadingService.setMessage('Deleting schedule ...');
    this.buildStandardHttpOptionsWithUser(username);
    return this.http.delete(`${ this.apiUrl }/import/${ importScheduleId }`, this.standardHttpOptionsWithUser).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public getImportLogs(importLogsSearchCriteria: ImportLogsSearchCriteria): Observable<any> {
    this.loadingService.setMessage('Searching logs ...');
    return this.http.post(`${ this.apiUrl }/import/logs`, JSON.stringify(importLogsSearchCriteria), standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  // TODO update Observable<any> to accurately reflect returned ata type
  public searchForThings(importSearchCriteria: ImportSearchCriteria): Observable<any> {
    this.loadingService.setMessage('Searching for things ...');
    return this.http.post(`${ this.apiUrl }/import/searchforthings`, JSON.stringify(importSearchCriteria), standardHttpOptions).pipe(
      map((response: Response) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }

  // TODO update Observable<any> to accurately reflect returned data type
  public getDataForThing(importDataCriteria: ImportDataCriteria): Observable<any> {
    this.loadingService.setMessage('Retrieving data ...');
    return this.http.post(`${ this.apiUrl }/import/getdataforthing`, JSON.stringify(importDataCriteria), standardHttpOptions).pipe(
      map((response: Response) => response == null ? 'success' : response),
      //catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }

  // TODO update Observable<any> to accurately reflect returned data type
  public getDataTypesForThing(importDataCriteria: ImportDataCriteria): Observable<any> {
    return this.http.post(`${ this.apiUrl }/import/getdatatypesforthing`, JSON.stringify(importDataCriteria), standardHttpOptions).pipe(
      map((response: Response) => response == null ? 'success' : response),
      //catchError(this.handleError),
    );
  }


  public importNow(importNowCriteria: ImportNowCriteria): Observable<any> {
    this.loadingService.setMessage('Importing data ...');
    return this.http.post(`${ this.apiUrl }/import/importnow`, JSON.stringify(importNowCriteria), standardHttpOptions).pipe(
      //map((response: Response) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public analyzeCSVFileContent(importFileAnalysis: ImportCSVFileContent[]): Observable<any> {
    this.loadingService.setMessage('Analyzing file content ...');
    return this.http.post(`${ this.apiUrl }/import/analyzefilecontent`, JSON.stringify(importFileAnalysis), standardHttpOptions).pipe(
      map((response: Response) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public bulkCSVImport(importFileAnalysis: ImportCSVFileContent[]) {
    this.loadingService.setMessage('Importing CSV data ...');
    return this.http.post(`${ this.apiUrl }/import/bulkcsvimport`, JSON.stringify(importFileAnalysis), standardHttpOptions).pipe(
      map((response: Response) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }

  
  public compareSyncMcSensors(deviceClass: string, saveSync: boolean): Observable<CompareSyncResults> {
    this.loadingService.setMessage('Contacting MC, Particle, and local clouds ...');
    return this.http.get<CompareSyncResults>(`${ this.apiUrl }/import/compareSyncMcSensors/${ deviceClass }/${ saveSync }`, standardGetHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public syncMcSensor(sensorUid: string): Observable<CompareSyncResults> {
    this.loadingService.setMessage('Contacting MC, Particle, and local clouds ...');
    return this.http.get<CompareSyncResults>(`${ this.apiUrl }/import/syncMcSensor/${ sensorUid }`, standardGetHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }

  // Test
  public importAll(): Observable<any> {
    var dateString = moment.utc(new Date()).format('YYYY-MM-DDTHH:mm:ss');
    this.loadingService.setMessage('Import all ...');
    return this.http.get(`${ this.apiUrl }/import/importitems/${ dateString }`, standardGetHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  private handleError(error: any) {
    if (error.status === 404) {
      return of(null);
    }
    return throwError(() => error);
  }
}
